import React, {useEffect, useRef, useState} from 'react';
import {
	getFromStore,
	StorePath,
} from '../services/store.service';
import {getClientDisplayName} from '../services/settings.service';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faClose} from '@fortawesome/free-solid-svg-icons/faClose';
import {faPaperPlane} from '@fortawesome/free-regular-svg-icons/faPaperPlane';
import {ROUTES} from '../models/routes.const';
import {useNavigate} from 'react-router';
import {OUTGOING_ACTION, snippetService} from '../services/snippet.service';
import {VIEW_MODE} from '../models/snippet-payload.model';
import {oemPermitsEmojis} from '../services/oem.service';
import {logIconButtonClick} from '../services/analytics.service';
import {ICON_BUTTON_CLICK_TARGET} from '../models/cta.model';

const Notification = () => {
	const navigate = useNavigate();
	const pickupLine = getFromStore(StorePath.PICKUP_LINE) as string;
	const clientDisplayName = getClientDisplayName();
	const params = getFromStore(StorePath.TRACK_PARAMS) as {is_mobile: boolean};
	const {oems} = getFromStore(StorePath.TRACK_DATA) as {oems: string};
	const isMobile = params.is_mobile;
	const componentRef = useRef<HTMLDivElement>(null);
	const [componentHeight, setComponentHeight] = useState(0);

	useEffect(() => {
		updateIframeViewMode(VIEW_MODE.NOTIFICATION);

		const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
			for (const entry of entries) {
				if (entry.target === componentRef.current) {
					const height: number = entry.contentRect.height;
					if (height > componentHeight) {
						setComponentHeight(height);
						updateIframeHeight(height);
					}
				}
			}
		});

		// Observe the component's ref for height changes
		if (componentRef.current) {
			resizeObserver.observe(componentRef.current);
		}

		// Clean up the ResizeObserver on unmount
		return () => {
			resizeObserver.disconnect();
		};
	}, []);

	const updateIframeViewMode = (viewMode: VIEW_MODE): void => {
		snippetService.sendMessage({
			action: OUTGOING_ACTION.VIEW_MODE_UPDATE,
			payload: {viewMode},
		});
	};

	const updateIframeHeight = (height: number): void => {
		snippetService.sendMessage({
			action: OUTGOING_ACTION.RESIZE,
			payload: {height: `${height + 32}px`},
		});
	};

	const startChat = (): void => {
		snippetService.sendMessage({action: OUTGOING_ACTION.RESIZE});
		snippetService.sendMessage({
			action: OUTGOING_ACTION.VIEW_MODE_UPDATE,
			payload: {viewMode: VIEW_MODE.WIDGET},
		});
		navigate(ROUTES.CHAT);
	};

	const dismissNotification = (event: React.MouseEvent<HTMLButtonElement>): void => {
		event.stopPropagation();
		snippetService.sendMessage({action: OUTGOING_ACTION.HIDE});
		snippetService.sendMessage({action: OUTGOING_ACTION.PICKUP_LINE_DISMISSED});
		logIconButtonClick(ICON_BUTTON_CLICK_TARGET.CLOSE);
	};

	const showEmojis = oemPermitsEmojis(oems);

	return (
		<div ref={componentRef} className={`text-sm p-4 ${isMobile ? "shadow-lg" : ""}`} onClick={startChat}>
			<div className={`px-4 pt-4 bg-white rounded-t-md relative
					${isMobile ? "" : "pb-4 shadow-lg rounded-b-md mb-3"}`}>
				<div className="flex items-center gap-2">
					{showEmojis && (
						<div>👋</div>
					)}
					<div className="text-gray-500 mr-[16px]">{clientDisplayName} Chat Team</div>

					<button className="ml-auto text-gray-500 p-4 absolute top-0 right-0 cursor-pointer" style={{wordBreak: 'break-word'}} onClick={dismissNotification}>
						<FontAwesomeIcon icon={faClose}/>
					</button>
				</div>

				<div className="pt-4 leading-tight whitespace-pre-line">
					{(pickupLine ?? '').split('\n').map((line: string, index: number) => <div key={index}>{line}</div>)}
				</div>
			</div>

			<div className={`bg-white p-4 rounded-b-md ${isMobile ? "" : "rounded-t-md shadow-lg"}`}>
				<div className="flex items-center w-full py-3 px-4 border border-gray-300 rounded-full">
					<div className="grow text-gray-700">Type your message</div>
					<div className="text-primary hover:text-primary">
						<FontAwesomeIcon icon={faPaperPlane} size="lg"/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Notification;
